import React, { useState, useContext, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import logo from '../logo2.png';
import { AuthContext } from "./AuthContext";

const BUCKET_NAME = 'fileBucket';
const ROOT_PATH = '';

function safeSegment(str) {
    return encodeURIComponent(str.replace(/\s+/g, '_'));
}

function formatTimestamp(timestamp) {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString();
}

function isPrivilegedRole(role) {
    return role === 'admin' || role === 'superadmin';
}

function FileManager() {
    const { user, userRole } = useContext(AuthContext);

    // 文件管理相关状态
    const [currentPath, setCurrentPath] = useState(ROOT_PATH);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    // 新建文件夹
    const [newFolderName, setNewFolderName] = useState('');
    const [showFolderModal, setShowFolderModal] = useState(false);
    // 文件上传相关
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    // 重命名
    const [renameItem, setRenameItem] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    // 搜索与排序
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState('name_asc');
    // 预览
    const [previewUrl, setPreviewUrl] = useState('');
    const [previewFile, setPreviewFile] = useState(null);
    const [previewType, setPreviewType] = useState('');
    // 批量删除
    const [selectedItems, setSelectedItems] = useState([]);
    const [batchDeleteMode, setBatchDeleteMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [filePermissions, setFilePermissions] = useState({});
    const [fileInfo, setFileInfo] = useState({});

    // 分享
    const [shareLink, setShareLink] = useState('');
    const [shareFile, setShareFile] = useState('');

    // 用户管理（仅 superadmin 可见）
    const [showUserManagement, setShowUserManagement] = useState(false);
    const [users, setUsers] = useState([]);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserRole, setNewUserRole] = useState('admin');

    // 提示信息弹窗
    const [modalMessage, setModalMessage] = useState(null);

    // 当 currentPath 改变时
    useEffect(() => {
        // 默认上传目标已为当前文件夹
    }, [currentPath]);

    // 防抖搜索
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const breadcrumbPaths = currentPath ? currentPath.split('/').filter(Boolean) : [];

    const handleGoBack = () => {
        if (!currentPath) return;
        const parts = currentPath.split('/').filter(Boolean);
        parts.pop();
        setCurrentPath(parts.join('/'));
    };

    // 获取文件夹/文件列表及权限信息
    const fetchFilesAndFolders = async (path = '') => {
        setLoading(true);
        try {
            const safePath = path ? path.split('/').map(safeSegment).join('/') : '';
            const { data, error } = await supabase.storage
                .from(BUCKET_NAME)
                .list(safePath, { limit: 100, offset: 0, sortBy: { column: 'name', order: 'asc' } });
            if (error) {
                console.error('Error fetching list:', error);
                return;
            }
            const validItems = (data || []).filter(item => item.name !== '.keep');
            setItems(validItems);

            const itemPaths = validItems.map(item => {
                const safeFileName = safeSegment(item.name);
                return safePath ? `${safePath}/${safeFileName}` : safeFileName;
            });

            if (itemPaths.length > 0) {
                const { data: permissionsData, error: permissionsError } = await supabase
                    .from('file_permissions')
                    .select('*')
                    .in('file_path', itemPaths);
                if (permissionsError) {
                    console.error('Error fetching file permissions:', permissionsError);
                } else {
                    const permissionsMap = {};
                    permissionsData.forEach(permission => {
                        permissionsMap[permission.file_path] = {
                            safety_officer: permission.safety_officer_visible,
                            project_manager: permission.project_manager_visible,
                            finance: permission.finance_visible,
                            hr: permission.hr_visible,
                        };
                    });
                    setFilePermissions(permissionsMap);
                }
            } else {
                setFilePermissions({});
            }

            if (itemPaths.length > 0) {
                const { data: infoData, error: infoError } = await supabase
                    .from('file_info')
                    .select('*')
                    .in('file_path', itemPaths);
                if (infoError) {
                    console.error('Error fetching file info:', infoError);
                    setFileInfo({});
                } else {
                    const infoMap = {};
                    infoData.forEach(info => {
                        infoMap[info.file_path] = {
                            created_by: info.created_by,
                            updated_by: info.updated_by,
                        };
                    });
                    setFileInfo(infoMap);
                }
            } else {
                setFileInfo({});
            }
        } catch (err) {
            console.error('fetchFilesAndFolders error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFilesAndFolders(currentPath);
        setSelectedItems([]);
        setCurrentPage(1);
    }, [currentPath]);

    // 用户管理 API
    const fetchAllUsers = async () => {
        try {
            let { data, error } = await supabase.from('users').select('*');
            if (error) throw error;
            setUsers(data || []);
        } catch (err) {
            console.error('fetchAllUsers error:', err);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!newUserEmail || !newUserPassword) return;
        try {
            const { error } = await supabase
                .from('users')
                .insert({
                    email: newUserEmail,
                    password: newUserPassword,
                    role: newUserRole
                });
            if (error) throw error;
            setNewUserEmail('');
            setNewUserPassword('');
            setNewUserRole('admin');
            fetchAllUsers();
        } catch (err) {
            console.error('handleCreateUser error:', err);
        }
    };

    const handleUpdateUserRole = async (userId, newRole) => {
        try {
            let { data, error } = await supabase
                .from('users')
                .update({ role: newRole })
                .eq('id', userId)
                .select();
            if (error) throw error;
            fetchAllUsers();
        } catch (err) {
            console.error('handleUpdateUserRole error:', err);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            let { error } = await supabase
                .from('users')
                .delete()
                .eq('id', userId);
            if (error) throw error;
            fetchAllUsers();
        } catch (err) {
            console.error('handleDeleteUser error:', err);
        }
    };

    const handleResetPassword = async (userId) => {
        const newPassword = window.prompt("Please enter a new password:");
        if (!newPassword) return;
        try {
            const { error } = await supabase
                .from('users')
                .update({ password: newPassword })
                .eq('id', userId);
            if (error) throw error;
            alert("Password reset successful");
            fetchAllUsers();
        } catch (err) {
            console.error("handleResetPassword error:", err);
            alert("Password reset failed");
        }
    };

    // 创建文件夹
    const handleCreateFolder = async (e) => {
        e.preventDefault();
        if (!newFolderName) return;
        const safeFolderName = safeSegment(newFolderName);
        const safePath = currentPath ? currentPath.split('/').map(safeSegment).join('/') : '';
        const folderPath = safePath ? `${safePath}/${safeFolderName}/.keep` : `${safeFolderName}/.keep`;
        const { error } = await supabase.storage
            .from(BUCKET_NAME)
            .upload(folderPath, new Blob(['']), { upsert: false });
        if (error) {
            console.error('Error creating folder:', error);
        } else {
            const folderPathForInfo = safePath ? `${safePath}/${safeFolderName}` : safeFolderName;
            const { error: infoError } = await supabase.from('file_info').upsert({
                file_path: folderPathForInfo,
                created_by: user?.email || 'unknown',
                updated_by: user?.email || 'unknown',
            });
            if (infoError) {
                console.error('Error inserting folder info:', infoError);
            }
            setNewFolderName('');
            setShowFolderModal(false);
            fetchFilesAndFolders(currentPath);
        }
    };

    // 文件上传
    const handleUpload = async (e) => {
        e.preventDefault();
        if (uploadFiles.length === 0) return;
        let successCount = 0;
        let errorCount = 0;
        setUploadProgress(0);
        try {
            for (let i = 0; i < uploadFiles.length; i++) {
                const { file, customName, uploadTo } = uploadFiles[i];
                const safePath = uploadTo || currentPath;
                const safeFileName = safeSegment(customName);
                const filePath = safePath ? `${safePath}/${safeFileName}` : safeFileName;
                const { error } = await supabase.storage
                    .from(BUCKET_NAME)
                    .upload(filePath, file, { upsert: true });
                if (error) {
                    errorCount++;
                    console.error('Error uploading file:', error);
                } else {
                    successCount++;
                    const { error: infoError } = await supabase.from('file_info').upsert({
                        file_path: filePath,
                        created_by: user?.email || 'unknown',
                        updated_by: user?.email || 'unknown',
                    });
                    if (infoError) {
                        console.error('Error inserting file info:', infoError);
                    }
                }
                setUploadProgress(Math.round(((i + 1) / uploadFiles.length) * 100));
            }
            if (successCount > 0 && errorCount === 0) {
                setModalMessage({ type: 'success', text: `Successfully uploaded ${successCount} files` });
            } else if (errorCount > 0 && successCount === 0) {
                setModalMessage({ type: 'error', text: `Failed to upload ${errorCount} files` });
            } else {
                setModalMessage({ type: 'warning', text: `Uploaded ${successCount} files, failed ${errorCount} files` });
            }
            setUploadFiles([]);
            setUploadProgress(0);
            fetchFilesAndFolders(currentPath);
        } catch (err) {
            console.error('handleUpload error:', err);
            setModalMessage({ type: 'error', text: 'An error occurred during upload' });
            setUploadProgress(0);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleFileSelect = (files) => {
        const filesWithNames = Array.from(files).map(file => ({
            file,
            customName: file.name,
            uploadTo: currentPath
        }));
        setUploadFiles(prev => [...prev, ...filesWithNames]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelect(e.dataTransfer.files);
            e.dataTransfer.clearData();
        }
    };

    const handleOpenFolder = (folderName) => {
        const decodedFolderName = decodeURIComponent(folderName);
        setCurrentPath(currentPath ? `${currentPath}/${decodedFolderName}` : decodedFolderName);
    };

    const handleBreadcrumbClick = (index) => {
        const newPath = breadcrumbPaths.slice(0, index + 1).join('/');
        setCurrentPath(newPath);
    };

    const handleDownload = async (fileName) => {
        const safePath = currentPath ? currentPath.split('/').map(safeSegment).join('/') : '';
        const safeFileName = safeSegment(fileName);
        const filePath = safePath ? `${safePath}/${safeFileName}` : safeFileName;
        const { data, error } = await supabase.storage
            .from(BUCKET_NAME)
            .download(filePath);
        if (error) {
            console.error('Download error:', error);
            return;
        }
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const handlePreview = async (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        if (!['jpg', 'jpeg', 'png', 'gif', 'pdf'].includes(ext)) return;
        const safePath = currentPath ? currentPath.split('/').map(safeSegment).join('/') : '';
        const safeFileName = safeSegment(fileName);
        const filePath = safePath ? `${safePath}/${safeFileName}` : safeFileName;
        const { data, error } = await supabase.storage
            .from(BUCKET_NAME)
            .download(filePath);
        if (error) {
            console.error('Preview error:', error);
            return;
        }
        const url = URL.createObjectURL(data);
        setPreviewUrl(url);
        setPreviewFile(fileName);
        setPreviewType(ext === 'pdf' ? 'pdf' : 'image');
    };

    const closePreview = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl('');
        setPreviewFile(null);
        setPreviewType('');
    };

    const handleRenameClick = (item) => {
        setRenameItem(item);
        setRenameValue(decodeURIComponent(item.name));
    };

    const handleRenameSubmit = async (e) => {
        e.preventDefault();
        if (!renameItem || !renameValue) {
            setRenameItem(null);
            setRenameValue('');
            return;
        }
        const safePath = currentPath ? currentPath.split('/').map(safeSegment).join('/') : '';
        const oldName = renameItem.name;
        const newName = safeSegment(renameValue);
        const itemType = renameItem.type;
        try {
            if (itemType === 'folder') {
                const oldFolderKeep = `${safePath ? `${safePath}/${oldName}` : oldName}/.keep`;
                const newFolderKeep = `${safePath ? `${safePath}/${newName}` : newName}/.keep`;
                const { error: moveError } = await supabase.storage
                    .from(BUCKET_NAME)
                    .move(oldFolderKeep, newFolderKeep);
                if (moveError) {
                    console.error('Folder rename error:', moveError);
                    setModalMessage({ type: 'error', text: 'Folder rename failed' });
                    return;
                } else {
                    const { error: removeError } = await supabase.storage
                        .from(BUCKET_NAME)
                        .remove([oldFolderKeep]);
                    if (removeError) {
                        console.error('Folder remove error:', removeError);
                    }
                }
                const oldFolderPathForInfo = safePath ? `${safePath}/${oldName}` : oldName;
                const newFolderPathForInfo = safePath ? `${safePath}/${newName}` : newName;
                const { error: updateInfoError } = await supabase.from('file_info').update({
                    file_path: newFolderPathForInfo,
                    updated_by: user?.email || 'unknown',
                }).eq('file_path', oldFolderPathForInfo);
                if (updateInfoError) {
                    console.error('Error updating folder info:', updateInfoError);
                    setModalMessage({ type: 'error', text: 'Folder rename failed' });
                    return;
                }
            } else {
                const oldPath = safePath ? `${safePath}/${oldName}` : oldName;
                const newPath = safePath ? `${safePath}/${newName}` : newName;
                const { error: fileMoveError } = await supabase.storage
                    .from(BUCKET_NAME)
                    .move(oldPath, newPath);
                if (fileMoveError) {
                    console.error('File rename error:', fileMoveError);
                    setModalMessage({ type: 'error', text: 'File rename failed' });
                    return;
                }
                const { error: updateInfoError } = await supabase.from('file_info').update({
                    file_path: newPath,
                    updated_by: user?.email || 'unknown',
                }).eq('file_path', oldPath);
                if (updateInfoError) {
                    console.error('Error updating file info:', updateInfoError);
                    setModalMessage({ type: 'error', text: 'File rename failed' });
                    return;
                }
            }
            fetchFilesAndFolders(currentPath);
            setModalMessage({ type: 'success', text: itemType === 'folder' ? 'Folder renamed successfully' : 'File renamed successfully' });
        } catch (err) {
            console.error('handleRenameSubmit error:', err);
            setModalMessage({ type: 'error', text: 'An error occurred during renaming' });
        } finally {
            setRenameItem(null);
            setRenameValue('');
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Are you sure you want to delete "${decodeURIComponent(item.name)}"?`)) return;
        const safePath = currentPath ? currentPath.split('/').map(safeSegment).join('/') : '';
        const safeName = safeSegment(decodeURIComponent(item.name));
        const itemPath = safePath ? `${safePath}/${safeName}` : safeName;
        let errorOccurred = false;
        try {
            if (item.type === 'folder') {
                const folderKeepFile = `${itemPath}/.keep`;
                const { error } = await supabase.storage
                    .from(BUCKET_NAME)
                    .remove([folderKeepFile]);
                if (error) {
                    console.error('Delete folder error:', error);
                    errorOccurred = true;
                }
            } else {
                const { error } = await supabase.storage
                    .from(BUCKET_NAME)
                    .remove([itemPath]);
                if (error) {
                    console.error('Delete file error:', error);
                    errorOccurred = true;
                }
            }
            fetchFilesAndFolders(currentPath);
            if (!errorOccurred) {
                setModalMessage({ type: 'success', text: item.type === 'folder' ? 'Folder deleted successfully' : 'File deleted successfully' });
            }
        } catch (err) {
            console.error(err);
            setModalMessage({ type: 'error', text: 'An error occurred during deletion' });
        }
    };

    const handleBatchDelete = async () => {
        if (selectedItems.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete the selected ${selectedItems.length} items?`)) return;
        let errorOccurred = false;
        for (const item of selectedItems) {
            const safePath = currentPath ? currentPath.split('/').map(safeSegment).join('/') : '';
            const safeName = safeSegment(decodeURIComponent(item.name));
            const itemPath = safePath ? `${safePath}/${safeName}` : safeName;
            try {
                if (item.type === 'folder') {
                    const folderKeepFile = `${itemPath}/.keep`;
                    const { error } = await supabase.storage
                        .from(BUCKET_NAME)
                        .remove([folderKeepFile]);
                    if (error) {
                        console.error(`Delete folder ${item.name} error:`, error);
                        errorOccurred = true;
                    }
                } else {
                    const { error } = await supabase.storage
                        .from(BUCKET_NAME)
                        .remove([itemPath]);
                    if (error) {
                        console.error(`Delete file ${item.name} error:`, error);
                        errorOccurred = true;
                    }
                }
            } catch (err) {
                console.error(err);
                errorOccurred = true;
            }
        }
        fetchFilesAndFolders(currentPath);
        setSelectedItems([]);
        if (!errorOccurred) {
            setModalMessage({ type: 'success', text: 'Selected items deleted successfully' });
        }
    };

    const handleSelectItem = (item, checked) => {
        if (checked) {
            setSelectedItems(prev => [...prev, item]);
        } else {
            setSelectedItems(prev => prev.filter(i => i.name !== item.name));
        }
    };

    const handlePermissionChange = async (filePath, role, newValue) => {
        const currentPerms = filePermissions[filePath] || {
            safety_officer: true,
            project_manager: true,
            finance: true,
            hr: true
        };

        const updatedPermissions = {
            file_path: filePath,
            safety_officer_visible: currentPerms.safety_officer,
            project_manager_visible: currentPerms.project_manager,
            finance_visible: currentPerms.finance,
            hr_visible: currentPerms.hr
        };

        if (role === 'safety_officer') {
            updatedPermissions.safety_officer_visible = newValue;
        } else if (role === 'project_manager') {
            updatedPermissions.project_manager_visible = newValue;
        } else if (role === 'finance') {
            updatedPermissions.finance_visible = newValue;
        } else if (role === 'hr') {
            updatedPermissions.hr_visible = newValue;
        }

        const { error } = await supabase
            .from('file_permissions')
            .upsert(updatedPermissions);
        if (error) {
            console.error("Error updating file permission", error);
        } else {
            setFilePermissions(prev => ({
                ...prev,
                [filePath]: {
                    safety_officer: updatedPermissions.safety_officer_visible,
                    project_manager: updatedPermissions.project_manager_visible,
                    finance: updatedPermissions.finance_visible,
                    hr: updatedPermissions.hr_visible
                }
            }));
        }
    };

    const handleShare = async (fileName) => {
        const safePath = currentPath ? currentPath.split('/').map(safeSegment).join('/') : '';
        const safeFileName = safeSegment(fileName);
        const filePath = safePath ? `${safePath}/${safeFileName}` : safeFileName;
        const { publicUrl } = supabase.storage.from(BUCKET_NAME).getPublicUrl(filePath);
        if (publicUrl) {
            setShareLink(publicUrl);
        } else {
            const { data, error } = await supabase.storage.from(BUCKET_NAME).createSignedUrl(filePath, 60);
            if (error) {
                console.error('Error creating signed URL:', error);
                return;
            }
            setShareLink(data.signedUrl);
        }
        setShareFile(fileName);
    };

    const filteredAndSortedItems = items
        .filter(item => {
            const decodedName = decodeURIComponent(item.name);
            if (!decodedName.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) return false;
            if (!isPrivilegedRole(userRole)) {
                const safePath = currentPath ? currentPath.split('/').map(safeSegment).join('/') : '';
                const safeFileName = safeSegment(item.name);
                const filePath = safePath ? `${safePath}/${safeFileName}` : safeFileName;
                const perms = filePermissions[filePath];
                if (perms && perms[userRole] === false) {
                    return false;
                }
            }
            return true;
        })
        .sort((a, b) => {
            switch (sortOption) {
                case 'name_asc':
                    return decodeURIComponent(a.name).localeCompare(decodeURIComponent(b.name));
                case 'name_desc':
                    return decodeURIComponent(b.name).localeCompare(decodeURIComponent(a.name));
                case 'created_at_asc':
                    return new Date(a.created_at) - new Date(b.created_at);
                case 'created_at_desc':
                    return new Date(b.created_at) - new Date(a.created_at);
                case 'updated_at_asc':
                    return new Date(a.updated_at) - new Date(b.updated_at);
                case 'updated_at_desc':
                    return new Date(b.updated_at) - new Date(a.updated_at);
                default:
                    return 0;
            }
        });

    const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
    const currentItems = filteredAndSortedItems.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Header Logo */}
            <header className="mb-6 text-center">
                <img src={logo} alt="Kindbridge Investments" className="w-64 mx-auto mb-4" />
            </header>

            {/* 文件上传部分 */}
            <section className="bg-white shadow rounded p-4 mb-6">
                {isPrivilegedRole(userRole) ? (
                    <div className="flex flex-col md:flex-row md:space-x-4">
                        <div className="flex-1">
                            <div
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                className="border-dashed border-2 border-gray-300 rounded p-4 mb-4"
                            >
                                <input
                                    type="file"
                                    multiple
                                    onChange={(e) => handleFileSelect(e.target.files)}
                                    className="focus:outline-none w-full"
                                />
                                <p className="text-xs text-gray-600 text-center">
                                    Drag files here or click to select files
                                </p>
                            </div>
                            {/* 上传文件列表 */}
                            {uploadFiles.length > 0 && (
                                <div className="mb-4">
                                    <h3 className="text-gray-800 font-bold mb-2">Files to Upload</h3>
                                   <h3 className="text-red-800 font-bold mb-2">NB: ALL FILE NAME MUST BE ENGLISH ONLY ! NAME IN CHINESE WILL CAUSE FAULT!</h3>
                                    <ul>
                                        {uploadFiles.map((item, index) => (
                                            <li key={index} className="flex items-center justify-between border p-2 rounded mb-2">
                                                <div className="flex-1">
                                                    <input
                                                        type="text"
                                                        value={item.customName}
                                                        onChange={(e) => {
                                                            const newName = e.target.value;
                                                            setUploadFiles(prev => {
                                                                const newArr = [...prev];
                                                                newArr[index].customName = newName;
                                                                return newArr;
                                                            });
                                                        }}
                                                        className="border border-gray-300 rounded p-1 mr-2"
                                                    />
                                                           
                                                    <label className="text-sm text-gray-700 mr-2">Upload to:</label>
                                                    <select
                                                        value={item.uploadTo}
                                                        onChange={(e) => {
                                                            const newUploadTo = e.target.value;
                                                            setUploadFiles(prev => {
                                                                const newArr = [...prev];
                                                                newArr[index].uploadTo = newUploadTo;
                                                                return newArr;
                                                            });
                                                        }}
                                                        className="border border-gray-300 rounded p-1"
                                                    >
                                                        <option value={currentPath}>{currentPath || 'Root'}</option>
                                                        {items.filter(folder => folder?.metadata === null).map(folder => {
                                                            const folderName = decodeURIComponent(folder.name);
                                                            const fullPath = currentPath ? `${currentPath}/${folderName}` : folderName;
                                                            return (
                                                                <option key={folder.name} value={fullPath}>
                                                                    {folderName}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                <button
                                                    onClick={() => setUploadFiles(prev => prev.filter((_, i) => i !== index))}
                                                    className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 ml-2"
                                                >
                                                    Remove
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <div className="w-full bg-gray-300 rounded h-4 mt-2">
                                    <div className="bg-blue-600 h-4 rounded" style={{ width: `${uploadProgress}%` }}></div>
                                </div>
                            )}
                            <button onClick={handleUpload} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mt-2">
                                Upload Files
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2">
                        <div
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            className="border-dashed border-2 border-gray-300 p-4 rounded w-full sm:w-auto"
                        >
                            <input
                                type="file"
                                multiple
                                onChange={(e) => handleFileSelect(e.target.files)}
                                className="focus:outline-none"
                            />
                            <p className="text-xs text-gray-600 text-center">
                                Drag files here or click to select files
                            </p>
                        </div>
                        <button onClick={handleUpload} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                            Upload Files
                        </button>
                    </div>
                )}
            </section>

            {/* 新建文件夹、用户管理及批量删除按钮 */}
            {isPrivilegedRole(userRole) && (
                <div className="mb-4 flex justify-end space-x-2">
                    <button
                        onClick={() => setShowFolderModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        New Folder
                    </button>
                    {userRole === 'superadmin' && (
                        <button
                            onClick={() => {
                                setShowUserManagement(true);
                                fetchAllUsers();
                            }}
                            className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-700"
                        >
                            Show User Management
                        </button>
                    )}
                    {!batchDeleteMode ? (
                        <button
                            onClick={() => setBatchDeleteMode(true)}
                            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                        >
                            Activate Batch Delete
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={handleBatchDelete}
                                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                            >
                                Delete Selected
                            </button>
                            <button
                                onClick={() => { setBatchDeleteMode(false); setSelectedItems([]); }}
                                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                            >
                                Cancel Batch Delete
                            </button>
                        </>
                    )}
                </div>
            )}

            <section className="mb-4">
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
                    <input
                        type="text"
                        placeholder="Search files..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border border-gray-300 p-2 rounded w-full sm:w-1/2"
                    />
                    <select
                        value={sortOption}
                        onChange={(e) => setSortOption(e.target.value)}
                        className="border border-gray-300 p-2 rounded w-full sm:w-1/4"
                    >
                        <option value="name_asc">Name Ascending</option>
                        <option value="name_desc">Name Descending</option>
                        <option value="created_at_asc">Created Date Ascending</option>
                        <option value="created_at_desc">Created Date Descending</option>
                        <option value="updated_at_asc">Updated Date Ascending</option>
                        <option value="updated_at_desc">Updated Date Descending</option>
                    </select>
                </div>
            </section>

            {currentPath && (
                <div className="mb-4">
                    <button onClick={handleGoBack} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                        Go Back
                    </button>
                </div>
            )}

            <nav className="mb-4">
                <ul className="flex items-center space-x-2 text-gray-600 text-sm">
                    <li onClick={() => setCurrentPath('')} className="cursor-pointer hover:underline">
                        Root
                    </li>
                    {breadcrumbPaths.map((p, i) => (
                        <li key={i} className="flex items-center">
                            <span>/</span>
                            <span onClick={() => handleBreadcrumbClick(i)} className="cursor-pointer hover:underline ml-1">
                {p}
              </span>
                        </li>
                    ))}
                </ul>
            </nav>

            <main className="bg-white shadow rounded p-4">
                {loading ? (
                    <p className="text-center text-gray-600">Loading...</p>
                ) : (
                    <>
                        <ul>
                            {currentItems.map((item) => {
                                const isFolder = item?.metadata === null;
                                const decodedName = decodeURIComponent(item.name);
                                const ext = decodedName.split('.').pop().toLowerCase();
                                const canPreview = !isFolder && ['jpg', 'jpeg', 'png', 'gif', 'pdf'].includes(ext);
                                const safePath = currentPath ? currentPath.split('/').map(safeSegment).join('/') : '';
                                const safeFileName = safeSegment(decodedName);
                                const filePath = safePath ? `${safePath}/${safeFileName}` : safeFileName;

                                return (
                                    <li key={item.name} className="p-2 border-b last:border-none">
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center">
                                                {batchDeleteMode && isPrivilegedRole(userRole) && (
                                                    <input
                                                        type="checkbox"
                                                        className="mr-2"
                                                        onChange={(e) =>
                                                            handleSelectItem({ name: item.name, type: isFolder ? 'folder' : 'file' }, e.target.checked)
                                                        }
                                                        checked={selectedItems.some(i => i.name === item.name)}
                                                    />
                                                )}
                                                <div className="flex flex-col">
                                                    {renameItem?.name === item.name ? (
                                                        <form onSubmit={handleRenameSubmit}>
                                                            <input
                                                                type="text"
                                                                value={renameValue}
                                                                onChange={(e) => setRenameValue(e.target.value)}
                                                                autoFocus
                                                                className="border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                            />
                                                        </form>
                                                    ) : (
                                                        <span
                                                            onClick={
                                                                isFolder
                                                                    ? () => handleOpenFolder(item.name)
                                                                    : canPreview
                                                                        ? () => handlePreview(decodedName)
                                                                        : undefined
                                                            }
                                                            className={isFolder ? 'cursor-pointer text-blue-700 hover:underline' : canPreview ? 'cursor-pointer text-green-700 hover:underline' : 'text-gray-800'}
                                                        >
                              {decodedName}
                            </span>
                                                    )}
                                                    {!isFolder && (
                                                        <div className="text-xs text-gray-600">
                                                            Uploaded: {formatTimestamp(item.created_at)}
                                                        </div>
                                                    )}
                                                    {isPrivilegedRole(userRole) && (
                                                        <div className="flex items-center space-x-4 text-gray-600 text-sm">
                                                            <label className="flex items-center space-x-1">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filePermissions[filePath]?.safety_officer ?? true}
                                                                    onChange={(e) => handlePermissionChange(filePath, 'safety_officer', e.target.checked)}
                                                                />
                                                                <span>Safety Officer</span>
                                                            </label>
                                                            <label className="flex items-center space-x-1">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filePermissions[filePath]?.project_manager ?? true}
                                                                    onChange={(e) => handlePermissionChange(filePath, 'project_manager', e.target.checked)}
                                                                />
                                                                <span>Project Manager</span>
                                                            </label>
                                                            <label className="flex items-center space-x-1">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filePermissions[filePath]?.finance ?? true}
                                                                    onChange={(e) => handlePermissionChange(filePath, 'finance', e.target.checked)}
                                                                />
                                                                <span>Finance</span>
                                                            </label>
                                                            <label className="flex items-center space-x-1">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={filePermissions[filePath]?.hr ?? true}
                                                                    onChange={(e) => handlePermissionChange(filePath, 'hr', e.target.checked)}
                                                                />
                                                                <span>HR</span>
                                                            </label>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex space-x-2">
                                                {(userRole === 'admin' || userRole === 'superadmin') && (
                                                    <>
                                                        {renameItem?.name === item.name ? (
                                                            <button
                                                                onClick={handleRenameSubmit}
                                                                className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700"
                                                            >
                                                                Save
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => handleRenameClick({ name: item.name, type: isFolder ? 'folder' : 'file' })}
                                                                className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700"
                                                            >
                                                                Rename
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                                {!isFolder && (
                                                    <>
                                                        <button
                                                            onClick={() => handleDownload(decodedName)}
                                                            className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700"
                                                        >
                                                            Download
                                                        </button>
                                                        <button
                                                            onClick={() => handleShare(decodedName)}
                                                            className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700"
                                                        >
                                                            Share
                                                        </button>
                                                    </>
                                                )}
                                                {(userRole === 'admin' || userRole === 'superadmin') && (
                                                    <button
                                                        onClick={() => handleDelete({ name: item.name, type: isFolder ? 'folder' : 'file' })}
                                                        className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700"
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                        {totalPages > 1 && (
                            <div className="mt-4 flex items-center justify-center space-x-4">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 border border-gray-300 rounded ${currentPage === 1 ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'}`}
                                >
                                    Previous
                                </button>
                                <span>
                  Page {currentPage} of {totalPages}
                </span>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className={`px-3 py-1 border border-gray-300 rounded ${currentPage === totalPages ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'}`}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* 提示信息弹窗 */}
            {modalMessage && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-4 rounded relative max-w-md w-full">
                        <h3 className="mb-2 text-lg font-semibold">
                            {modalMessage.type === 'success'
                                ? 'Success'
                                : modalMessage.type === 'error'
                                    ? 'Error'
                                    : 'Warning'}
                        </h3>
                        <p className="mb-4">{modalMessage.text}</p>
                        <button onClick={() => setModalMessage(null)} className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700">
                            Close
                        </button>
                    </div>
                </div>
            )}

            {previewUrl && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-4 rounded relative max-w-full max-h-full overflow-auto">
                        <h3 className="mb-2 text-lg font-semibold">{previewFile}</h3>
                        {previewType === 'pdf' ? (
                            <iframe src={previewUrl} title={previewFile} className="w-full h-[80vh]" />
                        ) : (
                            <img src={previewUrl} alt={previewFile} className="max-w-full max-h-[80vh]" />
                        )}
                        <button
                            onClick={closePreview}
                            className="absolute top-2 right-2 bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-700"
                        >
                            X
                        </button>
                    </div>
                </div>
            )}

            {shareLink && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-4 rounded relative max-w-md w-full">
                        <h3 className="mb-2 text-lg font-semibold">Share Link for {shareFile}</h3>
                        <input type="text" value={shareLink} readOnly className="border border-gray-300 p-2 w-full mb-4" />
                        <button
                            onClick={() => { navigator.clipboard.writeText(shareLink); }}
                            className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700 mr-2"
                        >
                            Copy Link
                        </button>
                        <button
                            onClick={() => setShareLink('')}
                            className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* 新建文件夹弹窗 */}
            {showFolderModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded shadow-md w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">New Folder</h2>
                        <form onSubmit={handleCreateFolder}>
                            <input
                                type="text"
                                placeholder="Enter folder name"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                className="border border-gray-300 p-2 rounded w-full mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                required
                            />
                            <div className="flex justify-end space-x-2">
                                <button
                                    type="button"
                                    onClick={() => { setShowFolderModal(false); setNewFolderName(''); }}
                                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* 用户管理弹窗（使用你提供的布局和表单） */}
            {showUserManagement && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white shadow rounded p-4 w-full max-w-3xl">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">User Management</h2>
                            <button onClick={() => setShowUserManagement(false)} className="text-gray-600 hover:text-gray-800 text-2xl">&times;</button>
                        </div>
                        <form onSubmit={handleCreateUser} className="flex flex-col sm:flex-row items-center mb-4 space-y-2 sm:space-y-0 sm:space-x-2">
                            <input
                                type="email"
                                placeholder="Email"
                                value={newUserEmail}
                                onChange={(e) => setNewUserEmail(e.target.value)}
                                className="border border-gray-300 p-2 rounded"
                                required
                            />
                            <input
                                type="text"
                                placeholder="Password"
                                value={newUserPassword}
                                onChange={(e) => setNewUserPassword(e.target.value)}
                                className="border border-gray-300 p-2 rounded"
                                required
                            />
                            <select
                                value={newUserRole}
                                onChange={(e) => setNewUserRole(e.target.value)}
                                className="border border-gray-300 p-2 rounded"
                            >
                                <option value="admin">admin</option>
                                <option value="HR">HR</option>
                                <option value="safety_officer">safety_officer</option>
                                <option value="project_manager">project_manager</option>
                                <option value="finance">finance</option>
                                <option value="superadmin">superadmin</option>
                            </select>
                            <button type="submit" className="bg-gray-800 text-white rounded px-4 py-2 hover:bg-gray-700">
                                Create User
                            </button>
                        </form>
                        <table className="w-full text-left border-collapse">
                            <thead>
                            <tr className="border-b">
                                <th className="p-2">Email</th>
                                <th className="p-2">Role</th>
                                <th className="p-2">Reset Password</th>
                                <th className="p-2">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {users.map((u) => (
                                <tr key={u.id} className="border-b">
                                    <td className="p-2">{u.email}</td>
                                    <td className="p-2">
                                        <select
                                            value={u.role}
                                            onChange={(e) => handleUpdateUserRole(u.id, e.target.value)}
                                            className="border border-gray-300 p-1 rounded"
                                        >
                                            <option value="admin">admin</option>
                                            <option value="HR">HR</option>
                                            <option value="safety_officer">safety_officer</option>
                                            <option value="project_manager">project_manager</option>
                                            <option value="finance">finance</option>
                                            <option value="superadmin">superadmin</option>
                                        </select>
                                    </td>
                                    <td className="p-2">
                                        <button
                                            onClick={() => handleResetPassword(u.id)}
                                            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                                        >
                                           Reset Password
                                        </button>
                                    </td>
                                    <td className="p-2">
                                        <button
                                            onClick={() => handleDeleteUser(u.id)}
                                            className="bg-gray-800 text-white px-3 py-1 rounded hover:bg-gray-700"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FileManager;
